import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import GlobalBluetoothWrapper from '../utils/GlobalBluetoothWrapper';
import { updateElapsedTime } from '../actions/activities.actions';
import { timerService } from '../services/TimerService';
import { 
  calculateForce, 
  getForceCalculationModeForWorkoutType, 
  // ForceCalculationMode,
  ForceCalculationResult 
} from '../utils/ForceCalculation';

// Types
interface SensorDataState {
  currentForce: number;
  processedForce: number | 'MAX' | 'Out';
  isDataStreamActive: boolean;
  lastUpdateTime: number;
  dataPoints: Array<{ x: number; y: number }>;
  workoutType: 'hangboard' | 'portable' | 'lift' | 'unknown';
  userWeight: number;
  addedWeights: number;
}

interface SensorDataContextType {
  // State
  currentForce: number;
  processedForce: number | 'MAX' | 'Out';
  isDataStreamActive: boolean;
  lastUpdateTime: number;
  dataPoints: Array<{ x: number; y: number }>;
  workoutType: 'hangboard' | 'portable' | 'lift' | 'unknown';
  userWeight: number;
  addedWeights: number;
  
  // Actions
  setWorkoutType: (type: 'hangboard' | 'portable' | 'lift' | 'unknown') => void;
  setCalibrationData: (userWeight: number, addedWeights: number) => void;
  setCalibrationDataFromExternal: (userWeight: number, addedWeights: number) => void;
  startDataStream: () => Promise<void>;
  stopDataStream: () => void;
  clearDataPoints: () => void;
  updateTimeDataPoints: (elapsedTime: number, forceValue?: number) => void;
  resetCalibrationData: () => void;
}

// Action Types
type SensorDataAction = 
  | { type: 'SET_CURRENT_FORCE'; payload: number }
  | { type: 'SET_PROCESSED_FORCE'; payload: number | 'MAX' | 'Out' }
  | { type: 'SET_DATA_STREAM_ACTIVE'; payload: boolean }
  | { type: 'SET_LAST_UPDATE_TIME'; payload: number }
  | { type: 'ADD_DATA_POINT'; payload: { x: number; y: number } }
  | { type: 'CLEAR_DATA_POINTS' }
  | { type: 'UPDATE_TIME_DATA_POINTS'; payload: { elapsedTime: number; forceValue?: number } }
  | { type: 'SET_WORKOUT_TYPE'; payload: 'hangboard' | 'portable' | 'lift' | 'unknown' }
  | { type: 'SET_CALIBRATION_DATA'; payload: { userWeight: number; addedWeights: number } };

// Initial State
const initialState: SensorDataState = {
  currentForce: 0,
  processedForce: 0,
  isDataStreamActive: false,
  lastUpdateTime: 0,
  dataPoints: [],
  workoutType: 'unknown',
  userWeight: 0,
  addedWeights: 0,
};

// Reducer
const sensorDataReducer = (state: SensorDataState, action: SensorDataAction): SensorDataState => {
  switch (action.type) {
    case 'SET_CURRENT_FORCE':
      return { ...state, currentForce: action.payload };
    case 'SET_PROCESSED_FORCE':
      return { ...state, processedForce: action.payload };
    case 'SET_DATA_STREAM_ACTIVE':
      return { ...state, isDataStreamActive: action.payload };
    case 'SET_LAST_UPDATE_TIME':
      return { ...state, lastUpdateTime: action.payload };
    case 'ADD_DATA_POINT':
      return { ...state, dataPoints: [...state.dataPoints, action.payload] };
    case 'CLEAR_DATA_POINTS':
      return { ...state, dataPoints: [] };
    case 'UPDATE_TIME_DATA_POINTS':
      // Add a new data point with the elapsed time and force value
      const { elapsedTime, forceValue } = action.payload;
      if (forceValue !== undefined) {
        // Add new data point
        return {
          ...state,
          dataPoints: [...state.dataPoints, { x: elapsedTime, y: forceValue }]
        };
      } else {
        // Update the last data point's time (legacy behavior)
        if (state.dataPoints.length > 0) {
          const lastDataPoint = state.dataPoints[state.dataPoints.length - 1];
          const updatedDataPoints = [...state.dataPoints];
          updatedDataPoints[updatedDataPoints.length - 1] = {
            ...lastDataPoint,
            x: elapsedTime
          };
          return {
            ...state,
            dataPoints: updatedDataPoints
          };
        }
      }
      return state;
    case 'SET_WORKOUT_TYPE':
      return { ...state, workoutType: action.payload };
    case 'SET_CALIBRATION_DATA':
      return { 
        ...state, 
        userWeight: action.payload.userWeight, 
        addedWeights: action.payload.addedWeights 
      };
    default:
      return state;
  }
};

// Context
const SensorDataContext = createContext<SensorDataContextType | undefined>(undefined);

// Provider Component
export const SensorDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(sensorDataReducer, initialState);
  const reduxDispatch = useDispatch();
  const globalBluetoothWrapper = GlobalBluetoothWrapper.getInstance();

  // Process force data using unified calculation system
  const processForceData = useCallback((rawForce: number, workoutType: string, userWeight: number, addedWeights: number): ForceCalculationResult => {
    const mode = getForceCalculationModeForWorkoutType(workoutType);
    
    const result = calculateForce({
      measuredForce: rawForce,
      userWeight,
      addedWeights,
      mode
    });
    
    // Removed excessive force calculation logging
    
    return result;
  }, []);

  // Handle incoming sensor data
  const handleSensorData = useCallback((data: any) => {
    try {
      let rawForce: number;
      
      // Check if data is already processed (number) or raw (DataView/ArrayBuffer)
      if (typeof data === 'number') {
        // Data is already processed by BluetoothWrapper
        rawForce = data;
      } else if (data instanceof DataView) {
        // Data is a DataView, process it
        rawForce = data.getUint16(0) / 100;
      } else if (data && data.byteLength !== undefined) {
        // Data is an ArrayBuffer, create DataView and process it
        const dataview = new DataView(data);
        rawForce = dataview.getUint16(0) / 100;
      } else {
        console.warn('SensorDataContext: Unknown data format:', typeof data, data);
        return;
      }
      
      // Validate data
      if (rawForce < 0 || rawForce > 1000) {
        console.warn('SensorDataContext: Invalid force data:', rawForce);
        return;
      }

      // Get the latest state values to ensure we're using current calibration data
      const currentState = globalBluetoothWrapper.getCalibrationState();
      const currentUserWeight = currentState.userWeight || state.userWeight;
      const currentAddedWeights = currentState.addedWeights || state.addedWeights;
      
      const forceResult = processForceData(rawForce, state.workoutType, currentUserWeight, currentAddedWeights);
      const currentTime = Date.now();

      dispatch({ type: 'SET_CURRENT_FORCE', payload: rawForce });
      dispatch({ type: 'SET_PROCESSED_FORCE', payload: forceResult.value });
      
      
      // Don't add data points here - let TimeDataSynchronizer handle time-based data points
      // The TimeDataSynchronizer will add data points with correct elapsed time
      // For now, just store the current force value for the current data point
      
      dispatch({ type: 'SET_LAST_UPDATE_TIME', payload: currentTime });
      
    } catch (error) {
      console.error('SensorDataContext: Error processing sensor data:', error);
    }
  }, [state.workoutType, state.userWeight, state.addedWeights, processForceData, globalBluetoothWrapper]);

  // Start data stream
  const startDataStream = useCallback(async () => {
    try {
      console.log('SensorDataContext: Starting data stream...');
      await globalBluetoothWrapper.ensureDataStreamActive(handleSensorData);
      dispatch({ type: 'SET_DATA_STREAM_ACTIVE', payload: true });
      console.log('SensorDataContext: Data stream started successfully - isDataStreamActive set to true');
    } catch (error) {
      console.error('SensorDataContext: Failed to start data stream:', error);
      dispatch({ type: 'SET_DATA_STREAM_ACTIVE', payload: false });
    }
  }, [globalBluetoothWrapper, handleSensorData]);

  // Stop data stream
  const stopDataStream = useCallback(() => {
    try {
      console.log('SensorDataContext: Stopping data stream...');
      globalBluetoothWrapper.stopDataStream();
      dispatch({ type: 'SET_DATA_STREAM_ACTIVE', payload: false });
      console.log('SensorDataContext: Data stream stopped');
    } catch (error) {
      console.error('SensorDataContext: Error stopping data stream:', error);
    }
  }, [globalBluetoothWrapper]);

  // Set workout type
  const setWorkoutType = useCallback((type: 'hangboard' | 'portable' | 'lift' | 'unknown') => {
    dispatch({ type: 'SET_WORKOUT_TYPE', payload: type });
  }, []);

  // Set calibration data
  const setCalibrationData = useCallback((userWeight: number, addedWeights: number) => {
    dispatch({ type: 'SET_CALIBRATION_DATA', payload: { userWeight, addedWeights } });
  }, []);

  // Removed Redux update mechanism that was overwriting per-rep elapsedTime 
  // with global session time, causing flickering.
  // TimerService and Timer.tsx already handle Redux timer updates.


  // Set calibration data from external source (like ExerciseManager)
  const setCalibrationDataFromExternal = useCallback((userWeight: number, addedWeights: number) => {
    if (userWeight > 0) {
      dispatch({ type: 'SET_CALIBRATION_DATA', payload: { userWeight, addedWeights } });
    }
  }, []);

  // Clear data points
  const clearDataPoints = useCallback(() => {
    dispatch({ type: 'CLEAR_DATA_POINTS' });
  }, []);

  // Update time data points with actual elapsed time
  const updateTimeDataPoints = useCallback((elapsedTime: number, forceValue?: number) => {
    dispatch({ type: 'UPDATE_TIME_DATA_POINTS', payload: { elapsedTime, forceValue } });
  }, []);

  // Reset calibration data for new exercise
  const resetCalibrationData = useCallback(() => {
    dispatch({ type: 'SET_CALIBRATION_DATA', payload: { userWeight: 0, addedWeights: 0 } });
  }, []);

  // Monitor Bluetooth connection state
  useEffect(() => {
    const checkBluetoothState = () => {
      const connectionState = globalBluetoothWrapper.getConnectionState();
      const calibrationState = globalBluetoothWrapper.getCalibrationState();
      
      if (connectionState.isConnected && calibrationState.isCalibrated) {
        // Update calibration data from Bluetooth wrapper only if it changed
        const newUserWeight = calibrationState.userWeight || 0;
        const newAddedWeights = calibrationState.addedWeights || 0;
        
        // Removed: Monitoring check log (too frequent)
        
    // Update from Bluetooth wrapper if:
    // 1. We don't have calibration data yet (userWeight === 0)
    // 2. OR the Bluetooth wrapper has a significantly different weight (new calibration)
    const hasNoCalibrationData = state.userWeight === 0 && state.addedWeights === 0;
    const hasSignificantWeightDifference = Math.abs(newUserWeight - state.userWeight) > 1.0; // More than 1kg difference
    
    if (hasNoCalibrationData || hasSignificantWeightDifference) {
      if (newUserWeight > 0) {
        setCalibrationData(newUserWeight, newAddedWeights);
      }
    }
    // Removed: Skipping calibration update log (too frequent)
        
        // Start data stream if not already active
        if (!state.isDataStreamActive) {
          startDataStream();
        }
      } else {
        // Stop data stream if not connected or calibrated
        if (state.isDataStreamActive) {
          stopDataStream();
        }
      }
    };

    // Check initial state
    checkBluetoothState();

    // Set up periodic check (every 2 seconds)
    const interval = setInterval(checkBluetoothState, 2000);

    return () => clearInterval(interval);
  }, [globalBluetoothWrapper, state.isDataStreamActive, state.addedWeights, state.userWeight, setCalibrationData, startDataStream, stopDataStream]);

  const contextValue: SensorDataContextType = {
    // State
    currentForce: state.currentForce,
    processedForce: state.processedForce,
    isDataStreamActive: state.isDataStreamActive,
    lastUpdateTime: state.lastUpdateTime,
    dataPoints: state.dataPoints,
    workoutType: state.workoutType,
    userWeight: state.userWeight,
    addedWeights: state.addedWeights,
    
    // Actions
    setWorkoutType,
    setCalibrationData,
    setCalibrationDataFromExternal,
    startDataStream,
    stopDataStream,
    clearDataPoints,
    updateTimeDataPoints,
    resetCalibrationData,
  };

  return (
    <SensorDataContext.Provider value={contextValue}>
      {children}
    </SensorDataContext.Provider>
  );
};

// Hook
export const useSensorData = (): SensorDataContextType => {
  const context = useContext(SensorDataContext);
  if (context === undefined) {
    throw new Error('useSensorData must be used within a SensorDataProvider');
  }
  return context;
};

export default SensorDataContext;
